import React from "react";
import { getTournamentTheme } from "../../../../styles/themes";

export default function MiniGamesStandings({ tournament }) {
    const theme = getTournamentTheme(tournament?.theme_key);
    const miniGames = tournament?.miniGames || [];
    const players = tournament?.playerData || [];

    const playerScores = players.map((player) => {
        let total = 0;
        const breakdown = {};

        miniGames.forEach((mini) => {
            let score = 0;

            for (const roundIdx in player.miniGameScores || {}) {
                score += player.miniGameScores[roundIdx]?.[mini.name] || 0;
            }

            breakdown[mini.name] = score;
            total += score;
        });

        return {
            id: player.id,
            name: player.name,
            total,
            breakdown,
        };
    });

    const sorted = [...playerScores].sort((a, b) => b.total - a.total);

    let currentRank = 1;
    let previousScore = null;
    let skippedPlaces = 0;

    sorted.forEach((player) => {
        if (player.total !== previousScore) {
            currentRank += skippedPlaces;
            player.rank = currentRank;
            skippedPlaces = 1;
        } else {
            player.rank = currentRank;
            skippedPlaces += 1;
        }

        previousScore = player.total;
    });

    const hasResults = sorted.some((player) => player.total > 0);

    const podium = hasResults
        ? sorted
            .filter((player) => player.rank <= 3)
            .sort((a, b) => a.rank - b.rank)
        : [];

    const rest = hasResults
        ? sorted.filter((player) => player.rank > 3)
        : [];

    const getMedal = (rank) => {
        if (rank === 1) return "🥇";
        if (rank === 2) return "🥈";
        if (rank === 3) return "🥉";
        return "";
    };

    const getPodiumStyle = (rank) => {
        if (rank === 1) {
            return {
                card: "border-[#d6ad35] bg-[#fffdf7] shadow-[0_12px_28px_rgba(120,91,16,0.18)]",
                header: "bg-[#fff5c9]",
                medal: "bg-[#47370a] text-[#ffe889]",
                rankText: "text-[#8a6710]",
                label: "1:a plats",
            };
        }

        if (rank === 2) {
            return {
                card: "border-[#b7bec3] bg-[#fcfdfd] shadow-[0_12px_28px_rgba(74,82,88,0.14)]",
                header: "bg-[#edf0f2]",
                medal: "bg-[#41494d] text-white",
                rankText: "text-[#687278]",
                label: "2:a plats",
            };
        }

        return {
            card: "border-[#c48357] bg-[#fffaf6] shadow-[0_12px_28px_rgba(113,65,31,0.15)]",
            header: "bg-[#f5e3d5]",
            medal: "bg-[#6b3216] text-[#ffe2cd]",
            rankText: "text-[#95552f]",
            label: "3:e plats",
        };
    };

    const renderStars = (
        value,
        large = false,
        highContrast = false,
        hideWhenEmpty = false
    ) => {
        const count = Number(value) || 0;

        if (count <= 0) {
            if (hideWhenEmpty) return null;

            return (
                <span
                    className={
                        highContrast
                            ? "text-[#9d9688] opacity-55"
                            : `${theme.textSecondary} opacity-45`
                    }
                >
                    –
                </span>
            );
        }

        const starClass = large
            ? "text-lg sm:text-xl"
            : "text-sm sm:text-base";

        const colorClass = highContrast
            ? "text-[#b27b08]"
            : theme.accent;

        return (
            <span
                className={`${colorClass} inline-flex max-w-full flex-wrap items-center justify-center gap-0.5 ${starClass} leading-none`}
                aria-label={`${count} ${count === 1 ? "vinst" : "vinster"}`}
                title={`${count} ${count === 1 ? "vinst" : "vinster"}`}
            >
                {Array.from({ length: count }, (_, index) => (
                    <span
                        key={index}
                        aria-hidden="true"
                        className="drop-shadow-[0_1px_1px_rgba(0,0,0,0.28)]"
                    >
                        ★
                    </span>
                ))}
            </span>
        );
    };

    if (miniGames.length === 0) {
        return (
            <section
                className={`${theme.section} ${theme.border} mt-6 rounded-2xl border px-6 py-12 text-center shadow-sm`}
            >
                <div
                    className={`${theme.primarySoft} ${theme.heading} mx-auto flex h-14 w-14 items-center justify-center rounded-full text-2xl`}
                >
                    ★
                </div>

                <h2 className={`${theme.heading} mt-4 text-xl font-semibold`}>
                    Inga sidotävlingar
                </h2>

                <p className={`${theme.textSecondary} mt-2 text-sm`}>
                    Den här turneringen har inga sidotävlingar ännu.
                </p>
            </section>
        );
    }

    return (
        <div className="mt-6 space-y-8">
            {/* Mini-games arena / podium */}
            <section
                className={`${theme.primary} ${theme.textOnPrimary} relative overflow-hidden rounded-2xl border border-white/10 px-4 py-6 shadow-lg sm:px-7 sm:py-7`}
            >
                {/* Decorative background */}
                <div
                    className={`${theme.accentBackground} pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full opacity-15 blur-3xl`}
                />
                <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

                <div className="relative">
                    <div className="text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/60 sm:text-[11px]">
                            Sidotävlingar
                        </p>

                        <h2 className="mt-1.5 text-2xl font-semibold tracking-tight sm:text-3xl">
                            Mini Games Arena
                        </h2>

                        <p className="mx-auto mt-1.5 max-w-xl text-sm leading-6 text-white/70">
                            Samlad ställning för turneringens sidotävlingar.
                        </p>

                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                            {miniGames.map((mini) => (
                                <span
                                    key={mini.name}
                                    className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-medium backdrop-blur-sm"
                                >
                                    {mini.name}
                                    {mini.subtract_from_score && (
                                        <span className="ml-1.5 text-white/60">
                                            −{mini.deduction_value || 0} slag
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>

                    {!hasResults ? (
                        <div className="mx-auto mt-8 max-w-lg rounded-2xl border border-white/15 bg-white/10 px-6 py-9 text-center backdrop-blur-sm">
                            <div className="text-4xl">🎯</div>
                            <h3 className="mt-3 text-lg font-semibold">
                                Inga resultat registrerade
                            </h3>
                            <p className="mt-1 text-sm text-white/65">
                                Podiet visas så snart någon vinner en sidotävling.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-7">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {podium.map((player) => {
                                    const podiumStyle = getPodiumStyle(player.rank);

                                    return (
                                        <article
                                            key={player.id}
                                            className={`${podiumStyle.card} min-w-0 overflow-hidden rounded-xl border transition duration-200 hover:-translate-y-0.5 hover:shadow-lg`}
                                        >
                                            {/* Spelaren får en egen tydlig rubrikyta */}
                                            <div
                                                className={`${podiumStyle.header} border-b border-black/10 px-3 py-3`}
                                            >
                                                <div className="flex items-start gap-2.5">
                                                    <span
                                                        className={`${podiumStyle.medal} inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg shadow-sm`}
                                                        aria-label={podiumStyle.label}
                                                    >
                                                        {getMedal(player.rank)}
                                                    </span>

                                                    <div className="min-w-0 flex-1">
                                                        <p
                                                            className="break-words text-base font-bold leading-snug text-[#25332a]"
                                                            title={player.name}
                                                        >
                                                            {player.name}
                                                        </p>

                                                        <p
                                                            className={`${podiumStyle.rankText} mt-0.5 text-[9px] font-bold uppercase tracking-[0.16em]`}
                                                        >
                                                            {podiumStyle.label}
                                                        </p>
                                                    </div>

                                                </div>
                                            </div>

                                            {/* Mini games ligger kompakt i två kolumner */}
                                            <div
                                                className={`grid gap-2 p-3 ${
                                                    miniGames.length === 1
                                                        ? "grid-cols-1"
                                                        : "grid-cols-2"
                                                }`}
                                            >
                                                {miniGames.map((mini, miniIndex) => (
                                                    <div
                                                        key={mini.name}
                                                        className={`flex min-h-[68px] min-w-0 flex-col items-center justify-between rounded-lg border border-[#e3ded2] bg-white px-2 py-2.5 text-center ${
                                                            miniGames.length > 1 &&
                                                            miniGames.length % 2 === 1 &&
                                                            miniIndex === miniGames.length - 1
                                                                ? "col-span-2"
                                                                : ""
                                                        }`}
                                                    >
                                                        <span
                                                            className="w-full break-words text-[9px] font-bold uppercase leading-tight tracking-[0.1em] text-[#706958]"
                                                            title={mini.name}
                                                        >
                                                            {mini.name}
                                                        </span>

                                                        <div className="mt-2">
                                                            {renderStars(
                                                                player.breakdown[
                                                                    mini.name
                                                                ],
                                                                false,
                                                                true
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Totalrad i stället för ännu ett stort innehållsblock */}
                                            <div className="flex items-center justify-between gap-3 border-t border-[#e3ded2] bg-[#f7f4ec] px-3 py-2.5">
                                                <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#706958]">
                                                    Totalt
                                                </span>

                                                {renderStars(
                                                    player.total,
                                                    false,
                                                    true
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Remaining standings */}
            {hasResults && (
                <section
                    className={`${theme.section} ${theme.border} overflow-hidden rounded-2xl border shadow-sm`}
                >
                    <div
                        className={`${theme.border} flex flex-col gap-3 border-b px-5 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-7`}
                    >
                        <div>
                            <p
                                className={`${theme.textSecondary} text-[10px] font-semibold uppercase tracking-[0.26em]`}
                            >
                                Leaderboard
                            </p>

                            <h3
                                className={`${theme.heading} mt-1 text-xl font-semibold tracking-tight sm:text-2xl`}
                            >
                                Övriga spelare
                            </h3>
                        </div>
                    </div>

                    {rest.length === 0 ? (
                        <div className="px-6 py-10 text-center">
                            <p className={`${theme.textSecondary} text-sm`}>
                                Alla spelare med registrerade vinster finns på
                                podiet.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-[700px] w-full border-collapse text-left text-sm">
                                <thead className={theme.tableHeader}>
                                    <tr>
                                        <th className="w-16 px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.16em] sm:px-4">
                                            Pos
                                        </th>

                                        <th className="sticky left-0 z-20 min-w-[180px] bg-inherit px-3 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] sm:px-4">
                                            Spelare
                                        </th>

                                        {miniGames.map((mini) => (
                                            <th
                                                key={mini.name}
                                                className="min-w-[100px] px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.14em]"
                                            >
                                                {mini.name}
                                            </th>
                                        ))}

                                        <th
                                            className={`${theme.primary} sticky right-0 z-20 min-w-[100px] border-l border-white/10 px-3 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.14em]`}
                                        >
                                            Totalt
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {rest.map((player, index) => {
                                        const rowBackground =
                                            index % 2 === 0
                                                ? theme.tableRow
                                                : theme.tableRowAlternate;

                                        return (
                                            <tr
                                                key={player.id}
                                                className={`${rowBackground} ${theme.border} border-b transition-colors hover:bg-black/[0.025]`}
                                            >
                                                <td className="px-3 py-3 text-center sm:px-4">
                                                    <span
                                                        className={`${theme.primarySoft} ${theme.heading} inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold tabular-nums`}
                                                    >
                                                        {player.rank}
                                                    </span>
                                                </td>

                                                <td
                                                    className={`${rowBackground} ${theme.textPrimary} sticky left-0 z-10 px-3 py-3 font-semibold sm:px-4`}
                                                >
                                                    {player.name}
                                                </td>

                                                {miniGames.map((mini) => (
                                                    <td
                                                        key={mini.name}
                                                        className="px-3 py-3 text-center"
                                                    >
                                                        {renderStars(
                                                            player.breakdown[
                                                                mini.name
                                                            ]
                                                        )}
                                                    </td>
                                                ))}

                                                <td
                                                    className={`${theme.stickyColumn} ${theme.border} sticky right-0 z-10 border-l px-3 py-3 text-center`}
                                                >
                                                    {renderStars(
                                                        player.total,
                                                        false,
                                                        false,
                                                        true
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}